import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Volume2, VolumeX, ArrowUp, Locate, AlertTriangle, Bus, ChevronDown, ChevronUp, PersonStanding } from 'lucide-react';
import { ManeuverIcon, TravelModeIcon } from './navigationIcons';
import { motion, useAnimation, type PanInfo } from 'framer-motion';
import mapboxgl from 'mapbox-gl';
import { useDirectionsStore } from '../../stores/directionsStore';
import { useMapStore } from '../../stores/mapStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { formatDistance as fmtDist, type TravelMode } from '../../services/mapbox';
import { MAPBOX_TOKEN } from '../../lib/env';
import { transformStyleUrls, resolveNavigationStyleUrl, MAP_MAX_ZOOM } from '../map/config/mapConfig';
import { clearMapboxTileCacheIfPromoted, fetchPromotedTileVersions } from '../../lib/tileVersions';
import { applyNavigationMapTweaks } from '../map/navigationMapTweaks';
import { applyCoordalCatalogIcons } from '../map/icons/applyCoordalIcons';
import { findCatalogEntryById, catalogLayerPath } from '../map/icons/iconCatalog';
import { loadCoordalSvgIcon } from '../map/icons/coordalIconLoader';

/** Haversine distance in metres between two lat/lng points */
function haversineMetres(
  lat1: number, lng1: number,
  lat2: number, lng2: number
): number {
  const R = 6_371_000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Minimum distance from route polyline in metres */
function distanceToRoute(
  lat: number, lng: number,
  coords: [number, number][]
): number {
  let min = Infinity;
  for (const [cLng, cLat] of coords) {
    const d = haversineMetres(lat, lng, cLat, cLng);
    if (d < min) min = d;
  }
  return min;
}

/** Sum step durations from the current step onward (live ETA). */
function sumRemainingDuration(steps: RouteStep[], fromIndex: number): number {
  let total = 0;
  for (let i = fromIndex; i < steps.length; i++) total += steps[i].duration ?? 0;
  return total;
}

function stopTypeLabel(type: string): string {
  const map: Record<string, string> = {
    bus_stop: 'Bus Stop',
    bus_station: 'Bus Station',
    trotro_stop: 'Bus Stop',
    taxi_rank: 'Taxi Rank',
    lorry_park: 'Lorry Park',
    rail_station: 'Train Station',
  };
  return map[type] ?? '';
}

interface RouteStep {
  instruction: string;
  distance: number;
  duration: number;
  mode?: string;
  stop_type?: string;
  maneuver: {
    type: string;
    modifier?: string;
    location: [number, number];
    bearing_after?: number;
  };
  name?: string;
}

function isValidHeading(heading: number | null | undefined): heading is number {
  return heading != null && Number.isFinite(heading) && heading >= 0;
}

/** Initial bearing in degrees (0 = north, clockwise). */
function bearingBetween(
  lat1: number, lng1: number,
  lat2: number, lng2: number,
): number {
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const lat1r = (lat1 * Math.PI) / 180;
  const lat2r = (lat2 * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos(lat2r);
  const x =
    Math.cos(lat1r) * Math.sin(lat2r) -
    Math.sin(lat1r) * Math.cos(lat2r) * Math.cos(dLng);
  return (Math.atan2(y, x) * 180) / Math.PI;
}

/** Bearing along the route polyline when GPS heading is unavailable (e.g. stationary). */
function bearingAlongRoute(
  lat: number,
  lng: number,
  coords: [number, number][],
): number | null {
  if (coords.length < 2) return null;

  let closestIdx = 0;
  let minDist = Infinity;
  for (let i = 0; i < coords.length; i++) {
    const [cLng, cLat] = coords[i];
    const d = haversineMetres(lat, lng, cLat, cLng);
    if (d < minDist) {
      minDist = d;
      closestIdx = i;
    }
  }

  const aheadIdx = Math.min(closestIdx + 1, coords.length - 1);
  if (aheadIdx === closestIdx) return null;
  const [aLng, aLat] = coords[aheadIdx];
  return bearingBetween(lat, lng, aLat, aLng);
}

function resolveNavigationBearing(
  heading: number | null | undefined,
  lat: number,
  lng: number,
  routeCoords: [number, number][],
  currentStep?: RouteStep,
  fallback = 0,
): number {
  if (isValidHeading(heading)) return heading;
  const routeBearing = bearingAlongRoute(lat, lng, routeCoords);
  if (routeBearing != null) return routeBearing;
  if (currentStep?.maneuver?.bearing_after != null) {
    return currentStep.maneuver.bearing_after;
  }
  return fallback;
}

function createNavUserMarkerElement(): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'nav-user-marker';
  el.innerHTML = '<div class="nav-user-arrow"></div>';
  return el;
}

function ensureNavMarkerStyles(): void {
  if (document.getElementById('nav-marker-styles')) return;
  const style = document.createElement('style');
  style.id = 'nav-marker-styles';
  style.textContent = `
    .nav-user-marker { width: 40px; height: 40px; }
    .nav-user-arrow {
      width: 0; height: 0;
      border-left: 20px solid transparent;
      border-right: 20px solid transparent;
      border-bottom: 40px solid #4285F4;
      filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
    }`;
  document.head.appendChild(style);
}

interface NavigationViewProps {
  destination: string;
  destinationCoords: { lat: number; lng: number };
  originCoords: { lat: number; lng: number };
  isDarkMode?: boolean;
  onClose: () => void;
}

// Get travel mode icon component
function getTravelModeIcon(mode: TravelMode, size: number = 20) {
  return <TravelModeIcon mode={mode} size={size} />;
}

// Get maneuver icon component based on type and modifier
function getManeuverIcon(
  type: string,
  modifier?: string,
  size: number = 32,
  className = 'text-white',
) {
  return (
    <ManeuverIcon
      type={type}
      modifier={modifier}
      size={size}
      className={className}
    />
  );
}

function formatDuration(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}min`;
  }
  return `${minutes} min`;
}

export function NavigationView({
  destination,
  destinationCoords,
  originCoords,
  isDarkMode = false,
  onClose,
}: NavigationViewProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [sheetSnap, setSheetSnap] = useState<'collapsed' | 'expanded'>('collapsed');
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [livePosition, setLivePosition] = useState<{ lat: number; lng: number } | null>(null);
  const [remainingDuration, setRemainingDuration] = useState(0);
  const [gpsUnavailable, setGpsUnavailable] = useState(false);
  const [isOffRoute, setIsOffRoute] = useState(false);
  const [isRerouting, setIsRerouting] = useState(false);
  const [isFollowing, setIsFollowing] = useState(true);
  // expandedSegments[driveStepIdx] = whether that drive step's stop sub-list is open
  const [expandedSegments, setExpandedSegments] = useState<Record<number, boolean>>({});
  const isFollowingRef = useRef(true);
  const watchIdRef = useRef<number | null>(null);
  const navUserMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const lastBearingRef = useRef(0);
  const rerouteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navSheetRef = useRef<HTMLDivElement>(null);
  const navSheetControls = useAnimation();
  const navSheetDragging = useRef(false);
  const currentStepIndexRef = useRef(0);
  const stepsRef = useRef<RouteStep[]>([]);
  const selectedRouteRef = useRef<typeof routes[number] | undefined>(undefined);
  const mapInstanceIdRef = useRef(0);
  const originCoordsRef = useRef(originCoords);
  const destinationCoordsRef = useRef(destinationCoords);
  const isDarkModeRef = useRef(isDarkMode);
  const previousDarkModeRef = useRef(isDarkMode);

  originCoordsRef.current = originCoords;
  destinationCoordsRef.current = destinationCoords;
  isDarkModeRef.current = isDarkMode;

  const { routes, selectedRouteIndex, travelMode, destination: destStore, fetchDirections, setOrigin } = useDirectionsStore();
  const { userLocation } = useMapStore();
  const { settings } = useSettingsStore();
  const formatDistance = (m: number) => fmtDist(m, settings.distanceUnit);
  const selectedRoute = routes[selectedRouteIndex];
  const displayDestination = destStore?.name || destination;

  const steps: RouteStep[] = selectedRoute?.steps || [];
  const currentStep = steps[currentStepIndex];
  const totalDuration = selectedRoute?.duration || 0;
  const totalDistance = selectedRoute?.distance || 0;

  currentStepIndexRef.current = currentStepIndex;
  stepsRef.current = steps;
  selectedRouteRef.current = selectedRoute;

  useEffect(() => {
    setRemainingDuration(selectedRoute?.duration ?? 0);
  }, [selectedRoute]);

  const NAV_SHEET_HEIGHTS = {
    collapsed: 'calc(9.5rem + env(safe-area-inset-bottom, 0px))',
    expanded: 'min(52vh, calc(100vh - 16rem))',
  } as const;

  const NAV_SHEET_SNAP = {
    collapsed: 0.14,
    expanded: 0.52,
  } as const;

  useEffect(() => {
    isFollowingRef.current = isFollowing;
  }, [isFollowing]);

  useEffect(() => {
    navSheetControls.start({
      y: 0,
      height: NAV_SHEET_HEIGHTS[sheetSnap],
      transition: { type: 'spring', damping: 32, stiffness: 320 },
    });
  }, [sheetSnap, navSheetControls]);

  const handleNavSheetDragStart = () => {
    navSheetDragging.current = true;
  };

  const handleNavSheetDragEnd = (_: unknown, info: PanInfo) => {
    navSheetDragging.current = false;
    const velocity = info.velocity.y;
    const offset = info.offset.y;
    const currentHeight = navSheetRef.current?.offsetHeight || 0;
    const viewportHeight = window.innerHeight;
    const draggedRatio = (currentHeight - offset) / viewportHeight;

    if (velocity < -400) {
      setSheetSnap('expanded');
      return;
    }
    if (velocity > 400) {
      setSheetSnap('collapsed');
      return;
    }

    const collapsedDist = Math.abs(draggedRatio - NAV_SHEET_SNAP.collapsed);
    const expandedDist = Math.abs(draggedRatio - NAV_SHEET_SNAP.expanded);
    setSheetSnap(collapsedDist < expandedDist ? 'collapsed' : 'expanded');
  };

  const handleNavSheetHandleClick = () => {
    if (navSheetDragging.current) return;
    setSheetSnap((prev) => (prev === 'collapsed' ? 'expanded' : 'collapsed'));
  };

  // Calculate ETA
  const displayRemaining = remainingDuration > 0 ? remainingDuration : totalDuration;
  const eta = new Date(Date.now() + displayRemaining * 1000).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  });

  const destroyNavigationMap = useCallback(() => {
    navUserMarkerRef.current?.remove();
    navUserMarkerRef.current = null;
    const activeMap = map.current;
    map.current = null;
    setIsMapLoaded(false);
    if (!activeMap) return;
    try {
      activeMap.remove();
    } catch {
      // Mapbox can throw if remove() races an in-flight style/sprites load.
    }
  }, []);

  const configureNavigationMap = useCallback((
    activeMap: mapboxgl.Map,
    instanceId: number,
    route: NonNullable<typeof selectedRouteRef.current>,
  ) => {
    if (mapInstanceIdRef.current !== instanceId || map.current !== activeMap) return;

    const origin = originCoordsRef.current;
    const destination = destinationCoordsRef.current;
    const routeCoords = route.geometry.coordinates as [number, number][];
    const routeSteps = route.steps || [];
    const initialBearing = resolveNavigationBearing(
      null,
      origin.lat,
      origin.lng,
      routeCoords,
      routeSteps[0],
    );
    lastBearingRef.current = initialBearing;

    const iconTheme = isDarkModeRef.current ? 'dark' : 'standard';
    const onImageMissing = (e: { id: string }) => {
      if (mapInstanceIdRef.current !== instanceId || map.current !== activeMap) return;
      const entry = findCatalogEntryById(e.id);
      if (!entry || entry.sprite) return;
      const basePath = catalogLayerPath(entry.layer, iconTheme);
      void loadCoordalSvgIcon(
        activeMap,
        entry.id,
        `${window.location.origin}${basePath}/${entry.file}`,
      ).catch(() => undefined);
    };
    activeMap.on('styleimagemissing', onImageMissing);

    const applyIcons = () => {
      if (mapInstanceIdRef.current !== instanceId || map.current !== activeMap) return;
      void applyCoordalCatalogIcons(activeMap, iconTheme).catch((err) => {
        console.warn('[navigation] coordal icon apply failed:', err);
      });
    };
    applyIcons();
    activeMap.once('idle', () => {
      if (mapInstanceIdRef.current !== instanceId || map.current !== activeMap) return;
      applyIcons();
      applyNavigationMapTweaks(activeMap);
    });
    applyNavigationMapTweaks(activeMap);

    if (activeMap.getSource('route')) {
      setIsMapLoaded(true);
      return;
    }

    activeMap.addSource('route', {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: route.geometry,
      },
    });

    activeMap.addLayer({
      id: 'route-line',
      type: 'line',
      source: 'route',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#4285F4',
        'line-width': 8,
        'line-opacity': 0.9,
      },
    });

    activeMap.addLayer({
      id: 'route-line-casing',
      type: 'line',
      source: 'route',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#041428',
        'line-width': 12,
        'line-opacity': 0.72,
      },
    }, 'route-line');

    new mapboxgl.Marker({ color: '#EA4335' })
      .setLngLat([destination.lng, destination.lat])
      .addTo(activeMap);

    ensureNavMarkerStyles();
    navUserMarkerRef.current = new mapboxgl.Marker({
      element: createNavUserMarkerElement(),
      rotationAlignment: 'map',
      pitchAlignment: 'map',
    })
      .setLngLat([origin.lng, origin.lat])
      .addTo(activeMap);

    activeMap.easeTo({
      center: [origin.lng, origin.lat],
      zoom: MAP_MAX_ZOOM,
      pitch: 60,
      bearing: initialBearing,
      duration: 800,
    });
    setIsMapLoaded(true);
  }, []);

  const loadNavigationStyle = useCallback(async (dark: boolean) => {
    const response = await fetch(`${resolveNavigationStyleUrl(dark)}?t=${Date.now()}`);
    if (!response.ok) {
      throw new Error(`Navigation style HTTP ${response.status}`);
    }
    const styleJson = await response.json();
    const { layers: layerVersions, cacheBust } = await fetchPromotedTileVersions();
    await clearMapboxTileCacheIfPromoted(cacheBust);
    return transformStyleUrls(styleJson, layerVersions);
  }, []);

  // Initialize navigation map once — avoid remounting on GPS/origin jitter (Mapbox
  // throws isIndoorEnabled errors when remove() races an in-flight style/sprites load).
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;
    const instanceId = ++mapInstanceIdRef.current;
    let cancelled = false;

    const route = selectedRouteRef.current;
    if (!route) return;

    const origin = originCoordsRef.current;
    const routeCoords = route.geometry.coordinates as [number, number][];
    const routeSteps = route.steps || [];
    const initialBearing = resolveNavigationBearing(
      null,
      origin.lat,
      origin.lng,
      routeCoords,
      routeSteps[0],
    );
    lastBearingRef.current = initialBearing;

    void loadNavigationStyle(isDarkModeRef.current)
      .then((transformedStyle) => {
        if (cancelled || mapInstanceIdRef.current !== instanceId || !mapContainer.current || map.current) {
          return;
        }

        const navMap = new mapboxgl.Map({
          container: mapContainer.current,
          style: transformedStyle,
          center: [origin.lng, origin.lat],
          zoom: MAP_MAX_ZOOM,
          pitch: 60,
          bearing: initialBearing,
          maxZoom: MAP_MAX_ZOOM,
          attributionControl: false,
          // @ts-expect-error Mapbox GL JS v3 indoor option is not in public types yet
          indoor: false,
        });

        if (cancelled || mapInstanceIdRef.current !== instanceId) {
          try {
            navMap.remove();
          } catch {
            // ignore teardown race
          }
          return;
        }

        map.current = navMap;

        navMap.addControl(new mapboxgl.AttributionControl({
          customAttribution: '© Coordal | Google Buildings | OpenStreetMap | Mapbox',
          compact: true,
        }), 'bottom-right');

        navMap.on('dragstart', () => setIsFollowing(false));
        navMap.on('load', () => {
          if (cancelled || mapInstanceIdRef.current !== instanceId || map.current !== navMap) return;
          const activeRoute = selectedRouteRef.current;
          if (!activeRoute) return;
          configureNavigationMap(navMap, instanceId, activeRoute);
        });
      })
      .catch((err) => console.error('Failed to load navigation style:', err));

    return () => {
      cancelled = true;
      mapInstanceIdRef.current += 1;
      destroyNavigationMap();
    };
  }, [configureNavigationMap, destroyNavigationMap, loadNavigationStyle]);

  // Theme swap without tearing down the map (prevents indoor/style teardown races).
  useEffect(() => {
    const activeMap = map.current;
    if (!activeMap || !isMapLoaded) return;
    if (previousDarkModeRef.current === isDarkMode) return;

    previousDarkModeRef.current = isDarkMode;
    isDarkModeRef.current = isDarkMode;

    let cancelled = false;
    void loadNavigationStyle(isDarkMode)
      .then((transformedStyle) => {
        if (cancelled || map.current !== activeMap) return;
        (activeMap as any).setStyle(transformedStyle, { diff: false });
        activeMap.once('style.load', () => {
          if (cancelled || map.current !== activeMap) return;
          const route = selectedRouteRef.current;
          if (!route) return;
          configureNavigationMap(activeMap, mapInstanceIdRef.current, route);
        });
      })
      .catch((err) => console.error('Failed to swap navigation style:', err));

    return () => {
      cancelled = true;
    };
  }, [configureNavigationMap, isDarkMode, isMapLoaded, loadNavigationStyle]);

  // Update route line when rerouting — keep map and GPS marker alive
  useEffect(() => {
    if (!map.current || !isMapLoaded || !selectedRoute) return;
    const source = map.current.getSource('route') as mapboxgl.GeoJSONSource | undefined;
    if (source) {
      source.setData({
        type: 'Feature',
        properties: {},
        geometry: selectedRoute.geometry,
      });
    }
  }, [selectedRoute, isMapLoaded]);

  // Real-time GPS tracking: advance steps, detect off-route, auto-reroute
  useEffect(() => {
    if (!selectedRoute) return;

    const onPosition = (pos: GeolocationPosition) => {
      setGpsUnavailable(false);
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      const heading = pos.coords.heading;
      setLivePosition({ lat, lng });

      const stepIdx = currentStepIndexRef.current;
      const activeSteps = stepsRef.current;
      const routeCoords = selectedRoute.geometry.coordinates as [number, number][];

      const bearing = resolveNavigationBearing(
        heading,
        lat,
        lng,
        routeCoords,
        activeSteps[stepIdx],
        lastBearingRef.current,
      );
      lastBearingRef.current = bearing;

      if (map.current) {
        navUserMarkerRef.current?.setLngLat([lng, lat]);
        navUserMarkerRef.current?.setRotation(bearing);

        if (isFollowingRef.current) {
          map.current.easeTo({
            center: [lng, lat],
            bearing,
            zoom: MAP_MAX_ZOOM,
            pitch: 60,
            duration: 800,
          });
        }
      }

      // Auto-advance step when within 30 m of next maneuver point
      let nextStepIdx = stepIdx;
      const nextIdx = stepIdx + 1;
      if (nextIdx < activeSteps.length) {
        const nextManeuver = activeSteps[nextIdx].maneuver.location;
        const dist = haversineMetres(lat, lng, nextManeuver[1], nextManeuver[0]);
        if (dist < 30) nextStepIdx = nextIdx;
      }
      if (nextStepIdx !== stepIdx) {
        setCurrentStepIndex(nextStepIdx);
        currentStepIndexRef.current = nextStepIdx;
      }
      setRemainingDuration(sumRemainingDuration(activeSteps, nextStepIdx));

      // Off-route detection: >80 m from route polyline
      const offRoute = distanceToRoute(lat, lng, routeCoords) > 80;
      setIsOffRoute(offRoute);

      if (offRoute && !isRerouting) {
        // Debounce reroute by 3 s to avoid spurious triggers
        if (rerouteTimeoutRef.current) clearTimeout(rerouteTimeoutRef.current);
        rerouteTimeoutRef.current = setTimeout(async () => {
          setIsRerouting(true);
          setOrigin({ coordinates: { lat, lng }, name: 'Current location' });
          await fetchDirections();
          setCurrentStepIndex(0);
          setIsOffRoute(false);
          setIsRerouting(false);
        }, 3000);
      } else if (!offRoute && rerouteTimeoutRef.current) {
        clearTimeout(rerouteTimeoutRef.current);
        rerouteTimeoutRef.current = null;
      }
    };

    const onError = () => setGpsUnavailable(true);

    watchIdRef.current = navigator.geolocation.watchPosition(onPosition, onError, {
      enableHighAccuracy: true,
      maximumAge: 2000,
      timeout: 10000,
    });

    return () => {
      if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);
      if (rerouteTimeoutRef.current) clearTimeout(rerouteTimeoutRef.current);
    };
  }, [selectedRoute, isRerouting, fetchDirections, setOrigin]);

  // Manual step advance (tap on banner)
  const goToNextStep = () => {
    if (currentStepIndex < steps.length - 1) {
      const nextIndex = currentStepIndex + 1;
      setCurrentStepIndex(nextIndex);
      const nextStep = steps[nextIndex];
      if (map.current && nextStep?.maneuver?.location) {
        map.current.flyTo({
          center: nextStep.maneuver.location,
          zoom: MAP_MAX_ZOOM,
          pitch: 60,
          duration: 1000,
        });
      }
    }
  };

  // Recenter map to current location and resume follow mode
  const handleRecenter = useCallback(() => {
    const location = livePosition || userLocation || originCoords;
    if (!map.current || !location) return;

    const routeCoords = selectedRoute?.geometry.coordinates as [number, number][] | undefined;
    const bearing = routeCoords
      ? resolveNavigationBearing(
        null,
        location.lat,
        location.lng,
        routeCoords,
        steps[currentStepIndex],
        lastBearingRef.current,
      )
      : lastBearingRef.current;

    lastBearingRef.current = bearing;
    setIsFollowing(true);
    map.current.flyTo({
      center: [location.lng, location.lat],
      zoom: MAP_MAX_ZOOM,
      pitch: 60,
      bearing,
      duration: 1000,
    });
  }, [livePosition, userLocation, originCoords, selectedRoute, steps, currentStepIndex]);

  if (!selectedRoute) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 bg-gray-900 z-50 flex items-center justify-center"
      >
        <div className="text-center text-white p-6">
          <p className="text-xl mb-4">No route available</p>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-white text-gray-900 rounded-lg font-medium"
          >
            Close
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex flex-col"
    >
      {/* Navigation Banner - Blue instruction bar like Mapbox */}
      <div className="bg-blue-600 text-white safe-area-top z-10">
        {/* Close button row */}
        <div className="flex items-center justify-between px-4 py-2">
          <button
            onClick={onClose}
            className="p-2 -ml-2 hover:bg-blue-700 rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
          <button
            onClick={() => setIsMuted(!isMuted)}
            className="p-2 -mr-2 hover:bg-blue-700 rounded-lg transition-colors"
          >
            {isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
          </button>
        </div>
        {gpsUnavailable && (
          <p className="px-4 pb-2 text-xs text-blue-100">
            Location signal lost — enable GPS or move to an open area.
          </p>
        )}

        {/* Off-route / rerouting banner */}
        {(isOffRoute || isRerouting) && (
          <div className="bg-amber-500 px-4 py-2 flex items-center gap-2">
            <AlertTriangle size={16} className="text-white flex-shrink-0" />
            <p className="text-white text-sm font-medium">
              {isRerouting ? 'Rerouting…' : 'Off route — rerouting in 3 s'}
            </p>
          </div>
        )}

        {/* Main instruction banner */}
        <div
          className="px-4 pb-4 cursor-pointer"
          onClick={goToNextStep}
        >
          <div className="flex items-center gap-4">
            {/* Maneuver icon */}
            <div className="w-16 h-16 bg-blue-700 rounded-xl flex items-center justify-center flex-shrink-0">
              {currentStep ? getManeuverIcon(currentStep.maneuver.type, currentStep.maneuver.modifier) : <ArrowUp size={32} className="text-white" />}
            </div>
            {/* Instruction text */}
            <div className="flex-1 min-w-0">
              <p className="text-3xl font-bold">
                {currentStep ? formatDistance(currentStep.distance) : '--'}
              </p>
              <p className="text-lg truncate">
                {currentStep?.name || currentStep?.instruction || 'Continue on route'}
              </p>
            </div>
          </div>
        </div>

        {/* Next step preview */}
        {steps[currentStepIndex + 1] && (
          <div className="bg-blue-700 px-4 py-2 flex items-center gap-3">
            <span className="text-blue-300 text-sm">Then</span>
            <div className="w-6 h-6 flex items-center justify-center">
              {getManeuverIcon(steps[currentStepIndex + 1].maneuver.type, steps[currentStepIndex + 1].maneuver.modifier, 20, 'text-white')}
            </div>
            <span className="text-sm truncate flex-1">{steps[currentStepIndex + 1].name || 'Continue'}</span>
          </div>
        )}
      </div>

      {/* Map Area */}
      <div className="flex-1 relative">
        <div ref={mapContainer} className="absolute inset-0" />

        {/* Recenter — shown when user pans away from follow mode */}
        {!isFollowing && (
          <button
            onClick={handleRecenter}
            className="absolute bottom-4 right-4 w-12 h-12 bg-white dark:bg-gray-800 rounded-full shadow-lg dark:shadow-gray-900/50 flex items-center justify-center text-blue-600 dark:text-blue-400 ring-2 ring-blue-500 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors z-10"
            title="Return to current location"
          >
            <Locate size={24} />
          </button>
        )}

        {!isMapLoaded && (
          <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
            <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full" />
          </div>
        )}
      </div>

      {/* Draggable bottom sheet — ETA summary + step list */}
      <motion.div
        ref={navSheetRef}
        initial={{ y: '100%' }}
        animate={navSheetControls}
        drag="y"
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={0.12}
        onDragStart={handleNavSheetDragStart}
        onDragEnd={handleNavSheetDragEnd}
        style={{ touchAction: 'none' }}
        className="fixed bottom-0 left-0 right-0 z-20 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl shadow-lg dark:shadow-gray-900/50 flex flex-col overflow-hidden safe-area-bottom rounded-t-2xl"
      >
        {/* Drag handle */}
        <button
          type="button"
          aria-label={sheetSnap === 'expanded' ? 'Collapse steps' : 'Expand steps'}
          onClick={handleNavSheetHandleClick}
          className="flex flex-col items-center pt-2.5 pb-1 cursor-grab active:cursor-grabbing flex-shrink-0 w-full"
        >
          <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
          <span className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
            {sheetSnap === 'expanded' ? 'Drag down' : `${steps.length} steps · drag up`}
          </span>
        </button>

        {/* Destination bar */}
        <div className="flex items-center gap-3 px-4 py-2 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
          <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400">
            {getTravelModeIcon(travelMode, 18)}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300 truncate flex-1">{displayDestination}</p>
        </div>

        {/* ETA Bar */}
        <div className="flex items-center justify-between p-4 flex-shrink-0">
          <div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatDuration(displayRemaining)}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{formatDistance(totalDistance)}</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">{eta}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Arrival</p>
          </div>
          <button
            onClick={onClose}
            className="ml-4 px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors"
          >
            End
          </button>
        </div>

        {/* Step list — visible when sheet expanded */}
        {sheetSnap === 'expanded' && (
          <div className="border-t border-gray-100 dark:border-gray-800 flex-1 min-h-0 overflow-y-auto">
            {steps.map((s, index) => {
              // Intermediate transit stops are rendered as sub-rows under the bus
              // driving step that precedes them — skip at the top level.
              const isIntermediate = s.mode === 'transit'
                && !s.instruction?.startsWith('Board here')
                && !s.instruction?.startsWith('Alight here');
              if (isIntermediate) return null;

              // Collect transit stops that immediately follow this bus driving step
              const ownedStops: RouteStep[] = [];
              if (s.mode === 'bus') {
                let j = index + 1;
                while (j < steps.length) {
                  const t = steps[j];
                  if (t.mode === 'transit'
                    && !t.instruction?.startsWith('Board here')
                    && !t.instruction?.startsWith('Alight here')) {
                    ownedStops.push(t); j++;
                  } else break;
                }
              }

              const isActive = index === currentStepIndex;
              const isBoard = s.mode === 'transit' && s.instruction?.startsWith('Board here');
              const isAlight = s.mode === 'transit' && s.instruction?.startsWith('Alight here');
              const segOpen = expandedSegments[index] ?? false;

              return (
                <div key={index}>
                  {/* Main step row */}
                  <div
                    onClick={() => {
                      setCurrentStepIndex(index);
                      if (map.current && s.maneuver?.location) {
                        map.current.flyTo({ center: s.maneuver.location, zoom: MAP_MAX_ZOOM, pitch: 60, duration: 1000 });
                      }
                    }}
                    className={`flex items-center gap-3 p-4 border-b border-gray-50 dark:border-gray-800 cursor-pointer transition-colors ${isBoard ? 'border-l-4 border-l-emerald-500 bg-emerald-50/60 dark:bg-emerald-900/10 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
                      : isAlight ? 'border-l-4 border-l-orange-500 bg-orange-50/60 dark:bg-orange-900/10 hover:bg-orange-50 dark:hover:bg-orange-900/20'
                        : isActive ? 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                  >
                    {/* Icon */}
                    {isBoard || isAlight ? (
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isBoard ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' : 'bg-orange-100 dark:bg-orange-900/30 text-orange-500 dark:text-orange-400'}`}>
                        <Bus size={16} />
                      </div>
                    ) : s.mode === 'walking' ? (
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-emerald-600 text-white' : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'}`}>
                        <PersonStanding size={18} />
                      </div>
                    ) : s.mode === 'bus' ? (
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-primary-600 text-white' : 'bg-primary-50 dark:bg-primary-900/20 text-primary-500 dark:text-primary-400'}`}>
                        <Bus size={16} />
                      </div>
                    ) : (
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}`}>
                        {getManeuverIcon(
                          s.maneuver.type,
                          s.maneuver.modifier,
                          18,
                          isActive ? 'text-white' : 'text-gray-600 dark:text-gray-300',
                        )}
                      </div>
                    )}

                    {/* Text */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm truncate ${s.mode === 'driving' ? 'opacity-60 text-gray-500 dark:text-gray-400'
                        : isActive ? 'font-semibold text-blue-700 dark:text-blue-400'
                          : 'text-gray-700 dark:text-gray-300'
                        }`}>
                        {s.mode === 'transit' ? s.instruction : (s.instruction || s.name || 'Continue')}
                      </p>
                      {(isBoard || isAlight) && s.stop_type && stopTypeLabel(s.stop_type) && (
                        <p className="text-xs text-primary-500 dark:text-primary-400 mt-0.5">{stopTypeLabel(s.stop_type)}</p>
                      )}
                      {/* Collapsed stop count sub-label */}
                      {s.mode === 'bus' && ownedStops.length > 0 && !segOpen && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                          {ownedStops.length} stop{ownedStops.length !== 1 ? 's' : ''}: {ownedStops.slice(0, 2).map(t => t.name || t.instruction?.replace(/^Next stop:\s*/, '')).filter(Boolean).join(' · ')}{ownedStops.length > 2 ? ` · +${ownedStops.length - 2}` : ''}
                        </p>
                      )}
                    </div>

                    {/* Expand chevron for bus steps with owned stops, or distance */}
                    {s.mode === 'bus' && ownedStops.length > 0 ? (
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); setExpandedSegments(prev => ({ ...prev, [index]: !prev[index] })); }}
                        className="ml-1 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 flex-shrink-0"
                      >
                        {segOpen ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                      </button>
                    ) : s.mode !== 'transit' && s.distance > 0 ? (
                      <span className={`text-sm flex-shrink-0 ${s.mode === 'driving' ? 'text-gray-400 opacity-60' : 'text-gray-500 dark:text-gray-400'}`}>
                        {formatDistance(s.distance)}
                      </span>
                    ) : null}
                  </div>

                  {/* Expandable stop list under this bus driving step */}
                  {s.mode === 'bus' && segOpen && ownedStops.length > 0 && (
                    <div className="bg-gray-50/60 dark:bg-gray-800/40 border-b border-gray-100 dark:border-gray-800">
                      {ownedStops.map((stop, si) => (
                        <button
                          type="button"
                          key={si}
                          onClick={() => {
                            if (map.current && stop.maneuver?.location) {
                              map.current.flyTo({ center: stop.maneuver.location, zoom: MAP_MAX_ZOOM, pitch: 60, duration: 800 });
                            }
                          }}
                          className="w-full flex items-center gap-3 pl-16 pr-4 py-2 hover:bg-primary-50/60 dark:hover:bg-primary-900/10 transition-colors text-left"
                        >
                          <div className="w-2 h-2 rounded-full bg-primary-300 dark:bg-primary-600 flex-shrink-0" />
                          <span className="text-xs text-gray-600 dark:text-gray-400 flex-1 truncate">
                            {stop.name || stop.instruction?.replace(/^Next stop:\s*/, '')}
                          </span>
                          {stop.stop_type && stopTypeLabel(stop.stop_type) && (
                            <span className="text-xs text-primary-400 dark:text-primary-500 flex-shrink-0">{stopTypeLabel(stop.stop_type)}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
